// ** react imports
import React from 'react';
import PropTypes from 'prop-types';

// ** components imports
import Loading from '../../../components/common/Loading';

// ** views imports
import TaxFileTableItem from './TaxFileTableItem';

const TaxFileTable = props => {
  // props
  const {
    data,
    onSortChange,
    currentSortBy,
    currentSortOrder,
    isGetAllTemplatesLoading,
    isGetAllTemplatesFetching,
  } = props;

  // Sort handler
  const handleSort = sortBy => {
    const newSortOrder =
      currentSortBy === sortBy && currentSortOrder === 'asc' ? 'desc' : 'asc';
    onSortChange(sortBy, newSortOrder);
  };

  // Get sort icon
  const getSortIcon = column => {
    if (currentSortBy !== column) {
      return (
        <svg
          className="w-4 h-4 ml-1"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"
          />
        </svg>
      );
    }
    return currentSortOrder === 'asc' ? (
      <svg
        className="w-4 h-4 ml-1 text-violet-500"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M5 15l7-7 7 7"
        />
      </svg>
    ) : (
      <svg
        className="w-4 h-4 ml-1 text-violet-500"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M19 9l-7 7-7-7"
        />
      </svg>
    );
  };

  const isLoading = isGetAllTemplatesLoading || isGetAllTemplatesFetching;

  return (
    <div className="bg-white h-[calc(70vh)] dark:bg-gray-800 shadow-xs rounded-xl relative overflow-y-auto">
      {isLoading && (
        <div className="absolute inset-0 bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm z-40 flex items-center justify-center rounded-xl">
          <div className="flex items-center gap-3 text-gray-600 dark:text-gray-300">
            <Loading />
          </div>
        </div>
      )}

      <div>
        <header className="px-5 py-4 bg-white dark:bg-gray-800 sticky top-0 z-10 border-b border-gray-100 dark:border-gray-700/60">
          <h2 className="font-semibold text-gray-800 dark:text-gray-100">
            All Tax Files{' '}
            <span className=" text-gray-400 dark:text-gray-500 font-medium">
              {data.total || 0}
            </span>
          </h2>
        </header>
        <div className="overflow-x-auto">
          <table className="table-fixed w-full dark:text-gray-300">
            <thead className="sticky top-0 z-10 text-xs font-semibold uppercase text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-900/20 border-t border-b border-gray-100 dark:border-gray-700/60">
              <tr>
                <th
                  className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700/20 uppercase w-24"
                  onClick={() => handleSort('referenceNumber')}
                >
                  <div className="font-semibold text-left flex items-center">
                    File Id #{getSortIcon('referenceNumber')}
                  </div>
                </th>
                <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap uppercase w-38">
                  <div className="font-semibold text-left">Customer Info</div>
                </th>
                <th
                  className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700/20 uppercase w-20"
                  onClick={() => handleSort('taxYear')}
                >
                  <div className="font-semibold text-left flex items-center">
                    Tax Year
                    {getSortIcon('taxYear')}
                  </div>
                </th>
                <th
                  className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700/20 uppercase w-24"
                  onClick={() => handleSort('submissionType')}
                >
                  <div className="font-semibold text-left flex items-center">
                    Type
                    {getSortIcon('submissionType')}
                  </div>
                </th>
                <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap uppercase w-40">
                  <div className="font-semibold text-left">Progress</div>
                </th>
                <th
                  className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700/20 uppercase w-28"
                  onClick={() => handleSort('fileStatus')}
                >
                  <div className="font-semibold text-left flex items-center">
                    Status
                    {getSortIcon('fileStatus')}
                  </div>
                </th>
                <th
                  className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700/20 uppercase w-32"
                  onClick={() => handleSort('createdAt')}
                >
                  <div className="font-semibold text-left flex items-center">
                    Created Date
                    {getSortIcon('createdAt')}
                  </div>
                </th>
                <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap uppercase w-16">
                  <div className="font-semibold text-center">Actions</div>
                </th>
              </tr>
            </thead>
            <tbody className="text-sm divide-y divide-gray-100 dark:divide-gray-700/60">
              {data.data && data.data.length > 0 ? (
                data.data.map((taxFile, index) => {
                  return (
                    <TaxFileTableItem
                      data={taxFile}
                      key={taxFile._id}
                      index={index}
                    />
                  );
                })
              ) : !isLoading ? (
                <tr className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                  <td colSpan="8" className="px-5 py-12 text-center">
                    <div className="flex flex-col items-center justify-center">
                      <svg
                        className="w-12 h-12 text-gray-400 dark:text-gray-500 mb-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                      <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                        No tax files found
                      </h3>
                      <p className="text-gray-500 dark:text-gray-400 text-sm">
                        Try adjusting your filters or search criteria to find
                        what you&apos;re looking for.
                      </p>
                    </div>
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

TaxFileTable.propTypes = {
  data: PropTypes.object,
  onSortChange: PropTypes.func,
  currentSortBy: PropTypes.string,
  currentSortOrder: PropTypes.string,
  isGetAllTemplatesLoading: PropTypes.bool,
  isGetAllTemplatesFetching: PropTypes.bool,
};

export default TaxFileTable;
